import { useState } from 'react';

import styles from './TabsDropdown.module.scss';

import { Badge } from '../../display/Badge';
import { FloatingBox } from '../../display/FloatingBox';
import { Tag } from '../../display/Tag';
import { LegacyIconButton } from '../LegacyIconButton';
import { Menu, MenuItem } from '../../forms/Menu';
import { useClickOutside } from '../../../hooks';

import type { TabsItemType, TabsSize, OnChooseItem } from './types';

type Props = {
  /**
   * Array of items to render in the dropdown.
   */
  items: Array<TabsItemType>;
  /**
   * Callback for when an item is chosen.
   */
  onChooseItem: OnChooseItem;
  /**
   * Indicates which item is selected based on its href.
   */
  selectedItemHref: string;
  /**
   * The size to render the dropdown at.
   */
  size: TabsSize;
};

export const TabsDropdown = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuItems = props.items.map((item) => (
    <MenuItem
      disabled={item.disabled}
      href={item.href}
      isSelected={item.href === props.selectedItemHref}
      key={item.href}
      onClick={(event: React.MouseEvent) => {
        setIsOpen(false);
        props.onChooseItem(item, event);
      }}
    >
      {item.text}
      {item.badgeText && (
        <Badge variant={item.badgeVariant} className={styles.badgeAndTag}>
          {item.badgeText}
        </Badge>
      )}
      {item.tagText && (
        <Tag variant={item.tagVariant} className={styles.badgeAndTag}>
          {item.tagText}
        </Tag>
      )}
    </MenuItem>
  ));
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => {
      setIsOpen(false);
    },
  });
  // Define handlers here to avoid non-applicable linter a11y complaints.
  const containerHandlers = {
    onKeyUp: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
  };
  return (
    <div ref={setClickOutsideNode} {...containerHandlers} data-zds>
      <LegacyIconButton
        color="icon-tertiary"
        icon="navMoreHoriz"
        label="More items…"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        size={'small'}
        tooltipPosition="west"
      />
      {isOpen && (
        <FloatingBox align="right">
          <div className={styles.menu} data-zds>
            <Menu aria-label="">{menuItems}</Menu>
          </div>
        </FloatingBox>
      )}
    </div>
  );
};
